import { useId, useMemo, useState } from 'react';
import type { Venue } from '../types';
import {
  type VenueActiveFilter,
  type VenueConfidenceFilter,
  type VenueFilterState,
  type VenueReviewStatusFilter,
  type VenueScanFreshness,
  type VenueSocialFilter,
  type VenueSortKey,
  countActiveVenueFilters,
} from '../lib/venueFilters';
import {
  type VenueGroupByKey,
  type VenueGroupByOption,
  ADMIN_VENUE_GROUP_BY_OPTIONS,
  PUBLIC_VENUE_GROUP_BY_OPTIONS,
} from '../lib/venueGrouping';
import { categoryOptions, formatCategory, getVenueCategories } from '../lib/venue-categories';
import DistanceControl from './DistanceControl';
import GroupBySelector from './GroupBySelector';
import type { Origin } from '../lib/eventFilters';

export type VenueFilterMode = 'public' | 'admin';

interface Props {
  /** Source venues — used to populate select options (neighborhoods, categories). */
  venues: Venue[];
  filters: VenueFilterState;
  setFilters: (updater: (prev: VenueFilterState) => VenueFilterState) => void;
  /** Public mode hides admin-only filters (active/confidence/scan freshness) and
   *  exposes a smaller grouping option list. */
  mode?: VenueFilterMode;
  /** When true the bar renders a multi-level group-by selector. */
  showGrouping?: boolean;
  groupBy?: VenueGroupByKey[];
  setGroupBy?: (next: VenueGroupByKey[]) => void;
  /** Optional sort dropdown — disabled when not provided. */
  showSort?: boolean;
  /** Custom sort options. Defaults to name + distance + neighborhood sorts. */
  sortOptions?: Array<{ value: VenueSortKey; label: string; requiresOrigin?: boolean }>;
  /** Right-aligned slot for view-toggle / count summary / extra controls. */
  toolbarRightSlot?: React.ReactNode;
  /** Hide the search input (e.g. when the host page already owns search). */
  hideSearch?: boolean;
  /** Hide the DistanceControl pill. */
  hideDistance?: boolean;
  /** When provided, the bar reads origin/distance from these props instead of
   *  `filters.origin` / `filters.maxDistanceMiles`, and routes DistanceControl
   *  changes to `onOriginChange` instead of `setFilters`. This lets a page
   *  source these values from a shared store (e.g. `useStoredOrigin`) without
   *  the round-trip bridge effects that previously caused render loops. */
  originOverride?: Origin;
  maxDistanceMilesOverride?: number;
  onOriginChange?: (next: { origin?: Origin; maxDistanceMiles?: number }) => void;
  /** ID prefix when more than one bar might exist on the same page. */
  idPrefix?: string;
}

const DEFAULT_SORT_OPTIONS: NonNullable<Props['sortOptions']> = [
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
  { value: 'neighborhood-asc', label: 'Neighborhood' },
  { value: 'distance-asc', label: 'Nearest', requiresOrigin: true },
];

/**
 * Shared search/filter toolbar for the venue Browse, Map, and Manage pages.
 * Mirrors the layout of `EventFiltersBar` so the two areas feel consistent.
 *
 * Layout:
 *   - Row 1: Search · Distance pill · Sort · "More filters" toggle · Reset · right slot
 *   - Row 2 (collapsed by default): chip rows for neighborhoods + categories +
 *     social presence; admin-mode adds active / confidence / scan freshness.
 *   - Row 3: optional multi-level group-by selector.
 */
export default function VenueFiltersBar({
  venues,
  filters,
  setFilters,
  mode = 'public',
  showGrouping = false,
  groupBy = [],
  setGroupBy,
  showSort = true,
  sortOptions = DEFAULT_SORT_OPTIONS,
  toolbarRightSlot,
  hideSearch = false,
  hideDistance = false,
  originOverride,
  maxDistanceMilesOverride,
  onOriginChange,
  idPrefix,
}: Props) {
  // Effective origin/distance: if the host page owns the value (passed via
  // `originOverride`), use that; otherwise fall back to filter state.
  const hasOriginOverride = onOriginChange !== undefined;
  const effectiveOrigin = hasOriginOverride ? originOverride : filters.origin;
  const effectiveMaxMiles = hasOriginOverride
    ? maxDistanceMilesOverride
    : filters.maxDistanceMiles;
  const generatedId = useId();
  const baseId = idPrefix ?? `mh-venuefilter-${generatedId}`;
  const [showMore, setShowMore] = useState(false);

  const activeCount = countActiveVenueFilters(filters);

  const allNeighborhoods = useMemo(
    () => Array.from(new Set(venues.map((v) => v.partitionKey).filter(Boolean))).sort(),
    [venues],
  );
  const allCategories = useMemo(
    () => categoryOptions(venues.flatMap(getVenueCategories)),
    [venues],
  );

  const updateSet = (key: 'neighborhoods' | 'categories', value: string) =>
    setFilters((prev) => {
      const next = new Set(prev[key]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, [key]: next };
    });

  const setSearch = (search: string) => setFilters((prev) => ({ ...prev, search }));
  const setSocial = (social: VenueSocialFilter) =>
    setFilters((prev) => ({ ...prev, social }));
  const setActive = (active: VenueActiveFilter) =>
    setFilters((prev) => ({ ...prev, active }));
  const setConfidence = (confidence: VenueConfidenceFilter) =>
    setFilters((prev) => ({ ...prev, confidence }));
  const setScan = (scanFreshness: VenueScanFreshness) =>
    setFilters((prev) => ({ ...prev, scanFreshness }));
  const setReviewStatus = (reviewStatus: VenueReviewStatusFilter) =>
    setFilters((prev) => ({ ...prev, reviewStatus }));
  const setSort = (sort: VenueSortKey) => setFilters((prev) => ({ ...prev, sort }));
  const setOriginAndRadius = (next: { origin?: Origin; maxDistanceMiles?: number }) => {
    if (onOriginChange) {
      onOriginChange(next);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      origin: next.origin,
      maxDistanceMiles: next.maxDistanceMiles,
    }));
  };

  const reset = () =>
    setFilters((prev) => ({
      ...prev,
      search: '',
      neighborhoods: new Set<string>(),
      categories: new Set<string>(),
      social: 'all',
      active: mode === 'admin' ? 'active' : 'all',
      confidence: 'all',
      scanFreshness: 'all',
      reviewStatus: 'all',
      // Keep origin/distance/sort — they're "view settings", not filter chips.
    }));

  const groupingOptions: VenueGroupByOption[] =
    mode === 'admin' ? ADMIN_VENUE_GROUP_BY_OPTIONS : PUBLIC_VENUE_GROUP_BY_OPTIONS;

  return (
    <div className="mh-venue-filters mb-3">
      {/* Toolbar row */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        {!hideSearch && (
          <>
            <label htmlFor={`${baseId}-search`} className="visually-hidden">
              Search venues
            </label>
            <input
              id={`${baseId}-search`}
              type="search"
              className="form-control form-control-sm"
              style={{ maxWidth: 260 }}
              placeholder="Search venues…"
              value={filters.search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </>
        )}

        {!hideDistance && (
          <DistanceControl
            origin={effectiveOrigin}
            maxDistanceMiles={effectiveMaxMiles}
            onChange={setOriginAndRadius}
            idPrefix={`${baseId}-distance`}
          />
        )}

        {showSort && (
          <>
            <label htmlFor={`${baseId}-sort`} className="visually-hidden">
              Sort by
            </label>
            <select
              id={`${baseId}-sort`}
              className="form-select form-select-sm w-auto"
              value={filters.sort}
              onChange={(e) => setSort(e.target.value as VenueSortKey)}
            >
              {sortOptions.map((o) => (
                <option
                  key={o.value}
                  value={o.value}
                  disabled={o.requiresOrigin && !effectiveOrigin}
                >
                  {o.label}
                  {o.requiresOrigin && !effectiveOrigin ? ' (set address)' : ''}
                </option>
              ))}
            </select>
          </>
        )}

        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          aria-expanded={showMore}
          aria-controls={`${baseId}-more`}
          onClick={() => setShowMore((s) => !s)}
        >
          {showMore ? 'Hide filters' : 'More filters'}
          {activeCount > 0 && (
            <span className="badge text-bg-primary ms-2">{activeCount}</span>
          )}
        </button>

        {activeCount > 0 && (
          <button type="button" className="btn btn-link btn-sm p-0" onClick={reset}>
            Reset
          </button>
        )}

        {toolbarRightSlot && <div className="ms-auto d-flex align-items-center gap-2">{toolbarRightSlot}</div>}
      </div>

      {/* Disclosure panel */}
      {showMore && (
        <div id={`${baseId}-more`} className="border rounded p-3 mb-2 bg-body-tertiary">
          {/* Neighborhoods */}
          {allNeighborhoods.length > 0 && (
            <fieldset className="mb-3">
              <legend className="form-label small fw-semibold mb-1">Neighborhoods</legend>
              <div className="d-flex flex-wrap gap-2">
                {allNeighborhoods.map((n) => {
                  const checked = filters.neighborhoods.has(n);
                  return (
                    <button
                      key={n}
                      type="button"
                      className={`btn btn-sm ${checked ? 'btn-primary' : 'btn-outline-secondary'}`}
                      aria-pressed={checked}
                      onClick={() => updateSet('neighborhoods', n)}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          {/* Categories */}
          {allCategories.length > 0 && (
            <fieldset className="mb-3">
              <legend className="form-label small fw-semibold mb-1">Categories</legend>
              <div className="d-flex flex-wrap gap-2">
                {allCategories.map((opt) => {
                  const checked = filters.categories.has(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`btn btn-sm ${checked ? 'btn-primary' : 'btn-outline-secondary'}`}
                      aria-pressed={checked}
                      onClick={() => updateSet('categories', opt.value)}
                    >
                      {formatCategory(opt.value)}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          {/* Social presence */}
          <fieldset className="mb-3">
            <legend className="form-label small fw-semibold mb-1">Social presence</legend>
            <div className="btn-group btn-group-sm" role="group" aria-label="Social presence filter">
              {(
                [
                  { v: 'all', l: 'All' },
                  { v: 'any', l: 'Any social' },
                  { v: 'both', l: 'IG + FB' },
                  { v: 'ig', l: 'Instagram' },
                  { v: 'fb', l: 'Facebook' },
                  { v: 'none', l: 'No social' },
                ] as const
              ).map(({ v, l }) => (
                <button
                  key={v}
                  type="button"
                  className={`btn ${filters.social === v ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setSocial(v)}
                  aria-pressed={filters.social === v}
                >
                  {l}
                </button>
              ))}
            </div>
          </fieldset>

          {mode === 'admin' && (
            <>
              {/* Active */}
              <fieldset className="mb-3">
                <legend className="form-label small fw-semibold mb-1">Active status</legend>
                <div className="btn-group btn-group-sm" role="group" aria-label="Active status filter">
                  {(
                    [
                      { v: 'active', l: 'Active' },
                      { v: 'inactive', l: 'Inactive' },
                      { v: 'all', l: 'All' },
                    ] as const
                  ).map(({ v, l }) => (
                    <button
                      key={v}
                      type="button"
                      className={`btn ${filters.active === v ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setActive(v)}
                      aria-pressed={filters.active === v}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Review status — admin-only. Disambiguates *why* a venue is
                  inactive: awaiting admin approval, explicitly declined, or
                  manually dismissed back when there was no review gate. */}
              <fieldset className="mb-3">
                <legend className="form-label small fw-semibold mb-1">Review status</legend>
                <div className="btn-group btn-group-sm flex-wrap" role="group" aria-label="Review status filter">
                  {(
                    [
                      { v: 'all', l: 'All' },
                      { v: 'pending', l: 'Pending review' },
                      { v: 'approved', l: 'Approved' },
                      { v: 'declined', l: 'Declined' },
                      { v: 'auto-declined', l: 'Auto-declined' },
                      { v: 'dismissed-legacy', l: 'Manually dismissed (legacy)' },
                    ] as const
                  ).map(({ v, l }) => (
                    <button
                      key={v}
                      type="button"
                      className={`btn ${filters.reviewStatus === v ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setReviewStatus(v)}
                      aria-pressed={filters.reviewStatus === v}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Confidence */}
              <fieldset className="mb-3">
                <legend className="form-label small fw-semibold mb-1">Confidence</legend>
                <div className="btn-group btn-group-sm" role="group" aria-label="Confidence filter">
                  {(
                    [
                      { v: 'all', l: 'All' },
                      { v: 'high', l: 'High' },
                      { v: 'medium', l: 'Medium' },
                      { v: 'low', l: 'Low' },
                      { v: 'closed', l: 'Closed' },
                    ] as const
                  ).map(({ v, l }) => (
                    <button
                      key={v}
                      type="button"
                      className={`btn ${filters.confidence === v ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setConfidence(v)}
                      aria-pressed={filters.confidence === v}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Scan freshness */}
              <fieldset>
                <legend className="form-label small fw-semibold mb-1">Scan freshness</legend>
                <div className="btn-group btn-group-sm" role="group" aria-label="Scan freshness filter">
                  {(
                    [
                      { v: 'all', l: 'All' },
                      { v: 'recent', l: 'Recent (≤14d)' },
                      { v: 'stale', l: 'Stale (>14d)' },
                      { v: 'never', l: 'Never' },
                    ] as const
                  ).map(({ v, l }) => (
                    <button
                      key={v}
                      type="button"
                      className={`btn ${filters.scanFreshness === v ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setScan(v)}
                      aria-pressed={filters.scanFreshness === v}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </fieldset>
            </>
          )}
        </div>
      )}

      {/* Group-by selector */}
      {showGrouping && setGroupBy && (
        <GroupBySelector<VenueGroupByKey>
          value={groupBy}
          onChange={setGroupBy}
          options={groupingOptions}
          idPrefix={`${baseId}-groupby`}
        />
      )}
    </div>
  );
}
